import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';

import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

import { useTranslation } from 'react-i18next';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import OutlinedInput from '@mui/material/OutlinedInput';
import Button from '@mui/material/Button';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Checkbox from '@mui/material/Checkbox';

import PhoneInputForm from '../UI/PhoneInputForm/PhoneInputForm';
import { getRegisterFormData } from '../../constants/RegisterForm';

import {
  getUserAccount,
  logIn,
  registerUser
} from 'store/user-actions';
import ErrorMessage from 'components/UI/ErrorMessage/ErrorMessage';
import Loading from 'components/UI/Loading/Loading';

import classes from './RegisterForm.module.css';

const RegisterForm = props => {
  const { t } = useTranslation(['register']);
  const [lada, setLada] = useState('');
  const [emailError, setEmailError] = useState(false);
  const [phoneError, setPhoneError] = useState(false);
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [sendData, setSendData] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowconfirmPassword] =
    useState(false);
  const [errorRegister, setErrorRegister] = useState(false);
  const { register, handleSubmit, watch } = useForm();
  const [
    userNameText,
    userPasswordText,
    userLastNameText,
    userEmailText,
    userConfirmEmailText,
    userConfirmPasswordText,
    userLanguage
  ] = watch([
    'userNameText',
    'userPasswordText',
    'userLastNameText',
    'userEmailText',
    'userConfirmEmailText',
    'userConfirmPasswordText',
    'userLanguage'
  ]);

  useEffect(() => {
    setErrorRegister(false);
  }, [
    userNameText,
    userPasswordText,
    userLastNameText,
    userEmailText,
    userConfirmEmailText,
    userConfirmPasswordText,
    userLanguage
  ]);

  useEffect(() => {
    props.changeName(userNameText);
  }, [userNameText]);

  useEffect(() => {
    props.changeLastName(userLastNameText);
  }, [userLastNameText]);

  useEffect(() => {
    if (emailError) {
      setEmailError(false);
    }
  }, [userEmailText]);

  const checkUserHandler = async (type, value) => {
    if (value) {
      const confirmNameData = await getUserAccount(value);
      if (confirmNameData) {
        if (type === 'email') {
          setEmailError(true);
        } else if (type === 'phone') {
          setPhoneError(true);
        }
        return true; // Usuario ya existe
      }
    }
    return false; // Usuario no existe
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowconfirmPassword(!showConfirmPassword);
  };

  const phoneHandler = (lada, phone) => {
    setLada(`+${lada}`);
    setPhone(phone);
    if (phoneError) {
      setPhoneError(false);
    }
  };

  const onSubmit = async data => {
    setSendData(true);
    const emailReg =
      /^(([^<>()[\]\\/.,;:\s@\\"]+(\.[^<>()[\]\\/.,;:\s@\\"]+)*)|(\\".+\\"))@(([^<>()[\]\\/.,;:\s@\\"]+\.)+[^<>()[\]\\/.,;:\s@\\"]{2,})?$/;

    if (data.userNameText.length == 0) {
      setMessage(t('name_cannot_be_empty'));
      setErrorRegister(true);
      setSendData(false);
      return false;
    }
    if (data.userLastNameText.length == 0) {
      setMessage(t('name_cannot_be_empty'));
      setErrorRegister(true);
      setSendData(false);
      return false;
    }
    if (!emailReg.test(data.userEmailText)) {
      setMessage(t('provide_valid_email'));
      setErrorRegister(true);
      setSendData(false);
      return false;
    }

    const emailExists = await checkUserHandler(
      'email',
      data.userEmailText
    );
    if (emailExists) {
      setMessage(t('existing_user'));
      setErrorRegister(true);
      setSendData(false);
      return false;
    }

    if (data.userEmailText !== data.userConfirmEmailText) {
      setMessage(t('emails_must_match'));
      setErrorRegister(true);
      setSendData(false);
      return false;
    }
    if (data.userPasswordText !== data.userConfirmPasswordText) {
      setMessage(t('password_must_match'));
      setErrorRegister(true);
      setSendData(false);
      return false;
    }
    if (data.userPasswordText.length < 8) {
      setMessage(t('at_least_characters'));
      setErrorRegister(true);
      setSendData(false);
      return false;
    }

    const phoneExists = await checkUserHandler('phone', phone);
    if (phoneExists) {
      setMessage(t('existing_phone'));
      setErrorRegister(true);
      setSendData(false);
      return false;
    }

    if (!data.terms) {
      setMessage(t('accpet_terms'));
      setErrorRegister(true);
      setSendData(false);
      return false;
    }

    const registerUserData = await registerUser(
      data.userNameText,
      data.userLastNameText,
      data.userEmailText,
      data.userPasswordText,
      lada,
      phone,
      'es',
      'MX',
      'MXN',
      '$',
      data.terms,
      data.code,
      '',
      0,
      'mayahii'
    );
    if (registerUserData) {
      logIn(data.userEmailText, data.userPasswordText);
    } else {
      setMessage(t('existing_user'));
      setErrorRegister(true);
      setSendData(false);
    }
  };

  return (
    <>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={classes.loginForm}
      >
        {getRegisterFormData().map(input =>
          input.type === 'password' ? (
            <OutlinedInput
              key={input.id}
              className={classes.textField}
              inputProps={{
                maxLength: 30
              }}
              {...register(`${input.id}`)}
              type={
                input.id === 'userPasswordText'
                  ? showPassword
                    ? 'text'
                    : 'password'
                  : showConfirmPassword
                  ? 'text'
                  : 'password'
              }
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    onClick={
                      input.id === 'userPasswordText'
                        ? handleClickShowPassword
                        : handleClickShowConfirmPassword
                    }
                    edge="end"
                  >
                    {input.id === 'userPasswordText' ? (
                      showPassword ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )
                    ) : showConfirmPassword ? (
                      <VisibilityOff />
                    ) : (
                      <Visibility />
                    )}
                  </IconButton>
                </InputAdornment>
              }
              placeholder={t(input.text)}
            />
          ) : (
            <>
              <OutlinedInput
                key={input.id}
                className={classes.textField}
                {...register(`${input.id}`)}
                placeholder={t(input.text)}
                error={input.id === 'userEmailText' && emailError}
                onInput={e => {
                  if (
                    input.id === 'userNameText' ||
                    input.id === 'userLastNameText'
                  ) {
                    e.target.value = e.target.value.replace(
                      /[^a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s]/g,
                      ''
                    );
                  }
                  if (
                    input.id === 'userEmailText' ||
                    input.id === 'userConfirmEmailText'
                  ) {
                    let email = e.target.value.replace(/\s+/g, '');
                    email = email
                      .normalize('NFD')
                      .replace(/[\u0300-\u036f]/g, '');
                    e.target.value = email;
                  }
                }}
              />
              {input.id === 'userEmailText' && emailError && (
                <div className={classes.warningTextContainer}>
                  <p className={classes.warningTextTitle}>
                    ¡Este correo ya está registrado!
                  </p>
                  <p className={classes.warningTextSubitle}>
                    Por favor ingresa un correo diferente o, si
                    necesitas ayuda, contacta a nuestro{' '}
                    <a
                      href="http://ayuda.mayahii.com/"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Centro de ayuda
                    </a>
                    .
                  </p>
                </div>
              )}
            </>
          )
        )}
        <Select
          defaultValue="es"
          placeholder="Idioma preferido"
          {...register('userLanguage')}
          className={classes.textField}
        >
          <MenuItem value="es">Español</MenuItem>
          <MenuItem value="en">English</MenuItem>
        </Select>
        <PhoneInputForm phoneHandler={phoneHandler} />
        {phoneError && (
          <div className={classes.warningTextContainer}>
            <p className={classes.warningTextTitle}>
              ¡Este número de teléfono ya está registrado!
            </p>
            <p className={classes.warningTextSubtitle}>
              Por favor ingresa un número diferente o, si necesitas
              ayuda, contacta a nuestro{' '}
              <a
                href="http://ayuda.mayahii.com/"
                target="_blank"
                rel="noreferrer"
              >
                Centro de ayuda
              </a>
              .
            </p>
          </div>
        )}
        <Checkbox
          className={classes.checkTerms}
          {...register('terms')}
        />
        <Button
          className={classes.btnTerms}
          component={Link}
          to="/terms"
        >
          {t('terms')}
        </Button>
        <div className={classes.btnContainer}>
          {!sendData ? (
            <Button className={classes.btnSend} type="submit">
              {t('create')}
            </Button>
          ) : (
            <Loading />
          )}
        </div>
        {errorRegister && (
          <ErrorMessage open={errorRegister} message={message} />
        )}
      </form>
    </>
  );
};

RegisterForm.propTypes = {
  // eslint-disable-next-line react/require-default-props
  typeAcademy: PropTypes.number,
  // eslint-disable-next-line react/require-default-props
  idAcademy: PropTypes.number,
  // eslint-disable-next-line react/require-default-props
  academyName: PropTypes.string,
  // eslint-disable-next-line react/require-default-props
  changeName: PropTypes.func,
  // eslint-disable-next-line react/require-default-props
  changeLastName: PropTypes.func
};

export default RegisterForm;
